import { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { TShirtIcon } from '@phosphor-icons/react'

import { BackListButton } from '@reveni-io/reveni-ui'

import FiltersSection from '@/common/components/FiltersSection/FiltersSection'
import AppContext from '@/common/context/app.context'
import useRegions from '@/common/hooks/useRegions/useRegions'
import useReturnPortalFeatures from '@/common/hooks/useReturnPortalFeatures/useReturnPortalFeatures'
import MostReturnedCard from '@/metrics/components/MostReturnedCard/MostReturnedCard'
import Product from '@/metrics/components/Product/Product'
import ProductMetrics from '@/metrics/components/ProductMetrics/ProductMetrics'
import { groupByValues } from '@/metrics/constants'
import { getTitleByOrdering } from '@/metrics/helpers/helpers'
import useFilters from '@/metrics/hooks/useFilters/useFilters'
import { entityTypes } from '@/metrics/hooks/useMetrics/constants'
import useMetrics from '@/metrics/hooks/useMetrics/useMetrics'
import metricsServices from '@/metrics/services/services'
import routes from '@/Router/routes.constants'

import * as Styles from './styles'

const ProductByTypeMetricsDetailPage = ({ isUnifiedDashboard }) => {
  const { typePosition, productPosition } = useParams()
  const { info } = useContext(AppContext)
  const { t } = useTranslation()
  const { regions } = useRegions()

  const { returnPortalFeatures } = useReturnPortalFeatures({ returnPortalId: info?.selectedReturnPortal?.id })
  const salesOrdersDataActivated = returnPortalFeatures?.sales_orders_data_activated

  const {
    commonFilters,
    setCommonFilters,
    getUrlSearchParams,
    productsFilters,
    returnSalesRatioFilters,
    setReturnSalesRatioFilters,
    returnsAndExchangesRatioFilters,
    setReturnsAndExchangesRatioFilters,
  } = useFilters()

  const { data: types, loading: loadingType } = useMetrics({
    returnPortalId: isUnifiedDashboard ? null : info?.selectedReturnPortal?.id,
    service: metricsServices.getTopReturnedProducts,
    entityType: entityTypes.product,
    filters: commonFilters,
    additionalFilters: { groupBy: groupByValues.types, ordering: productsFilters?.ordering },
    doRequest: !!typePosition,
  })

  const type = types?.results?.[typePosition]

  const { data: topReturnedProducts, loading: loadingTopReturnedProducts } = useMetrics({
    returnPortalId: isUnifiedDashboard ? null : info?.selectedReturnPortal?.id,
    service: metricsServices.getTypeTopReturnedProducts,
    entityId: type?.id,
    entityType: entityTypes.type,
    filters: commonFilters,
    additionalFilters: { ordering: productsFilters?.ordering },
    doRequest: !!type?.id,
  })

  const dashboardPath = isUnifiedDashboard ? routes.merchantDashboardPage : routes.dashboardPage
  const typePath = isUnifiedDashboard ? routes.merchantDashboardTypeMetricsDetailPage : routes.typeMetricsDetailPage

  const getDashboardUrl = () => {
    return `${dashboardPath}?${getUrlSearchParams()}`
  }

  const getBreadcrumbs = () => {
    return [
      {
        title: `#${Number(typePosition) + 1} ${t('Tipo')}`,
        subtitle: type?.name,
        url: `${typePath.replace(':typePosition', typePosition)}?${getUrlSearchParams()}`,
      },
      {
        title: `#${Number(productPosition) + 1} ${t('Producto')}`,
        subtitle: topReturnedProducts?.results?.[productPosition]?.name,
      },
    ]
  }

  return (
    <Styles.Container>
      <Styles.HeaderContainer>
        <BackListButton to={getDashboardUrl()}>{t('Volver al dashboard')}</BackListButton>
        <FiltersSection regions={regions} filters={commonFilters} onChange={setCommonFilters} />
      </Styles.HeaderContainer>
      <MostReturnedCard
        Icon={TShirtIcon}
        title={getTitleByOrdering('Tipos', productsFilters?.ordering, t)}
        loading={loadingType || loadingTopReturnedProducts}
        item={
          topReturnedProducts?.results?.[productPosition] && (
            <Product
              product={{ ...topReturnedProducts?.results?.[productPosition], index: Number(productPosition) + 1 }}
              showAmounts={false}
              isUnifiedDashboard={isUnifiedDashboard}
            />
          )
        }
        count={topReturnedProducts?.results?.[productPosition]?.count}
        percentage={topReturnedProducts?.results?.[productPosition]?.percentage}
        percentageText={t('tipo')}
        breadcrumbs={getBreadcrumbs()}
        sales={topReturnedProducts?.results?.[productPosition]?.sales}
        salesOrdersDataActivated={salesOrdersDataActivated || isUnifiedDashboard}
      />

      {topReturnedProducts?.results?.[productPosition] && (
        <ProductMetrics
          loading={loadingType || loadingTopReturnedProducts}
          returnPortalId={isUnifiedDashboard ? null : info?.selectedReturnPortal?.id}
          typeId={type?.id}
          typePosition={typePosition}
          product={topReturnedProducts?.results?.[productPosition]}
          productPosition={productPosition}
          commonFilters={commonFilters}
          returnSalesRatioFilters={returnSalesRatioFilters}
          setReturnSalesRatioFilters={setReturnSalesRatioFilters}
          returnsAndExchangesRatioFilters={returnsAndExchangesRatioFilters}
          setReturnsAndExchangesRatioFilters={setReturnsAndExchangesRatioFilters}
          storeCurrency={info?.selectedStore?.currency}
          variantsOrdering={productsFilters?.ordering}
        />
      )}
    </Styles.Container>
  )
}

export default ProductByTypeMetricsDetailPage
